import React, { useEffect, useMemo, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useCompetition } from "@/context/CompetitionContext";
import CompetitionSelector from "@/components/CompetitionSelector";
import { ListChecks, Search } from "lucide-react";

export default function OperatorAthletes() {
  const { currentId } = useCompetition();
  const [cats, setCats] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!currentId) return;
    setError("");
    Promise.all([
      api.get(`/competitions/${currentId}/categories`),
      api.get(`/competitions/${currentId}/athletes`),
    ])
      .then(([c, a]) => {
        setCats(c.data);
        setAthletes(a.data);
      })
      .catch((e) => setError(extractErrorMessage(e)));
  }, [currentId]);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    return [...cats]
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
      .filter((c) => !catFilter || c.id === catFilter)
      .map((c) => ({
        ...c,
        athletes: athletes
          .filter((a) => a.category_id === c.id)
          .filter(
            (a) =>
              !term ||
              (a.nome || "").toLowerCase().includes(term) ||
              (a.equipe || "").toLowerCase().includes(term) ||
              String(a.numero || "").includes(term)
          )
          .sort((a, b) => (a.ordem_palco ?? 999) - (b.ordem_palco ?? 999) || (a.numero || 0) - (b.numero || 0)),
      }))
      .filter((c) => c.athletes.length > 0 || (!term && !catFilter));
  }, [cats, athletes, q, catFilter]);

  return (
    <div data-testid="operator-athletes-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-1">
            <ListChecks size={12} /> Somente leitura
          </div>
          <h1 className="font-display text-5xl font-black tracking-tight">ATLETAS POR CATEGORIA</h1>
          <div className="text-xs text-[var(--text-secondary)] mt-1">
            {athletes.length} inscrição(ões) em {cats.length} categoria(s)
          </div>
        </div>
        <CompetitionSelector />
      </div>

      <div className="card p-4 mb-6 grid grid-cols-1 md:grid-cols-[1fr_260px] gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
          <input
            data-testid="op-athletes-search"
            className="input-field !pl-9"
            placeholder="Buscar por nome, equipe ou número"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="op-athletes-cat-filter"
          className="input-field"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
        >
          <option value="">Todas as categorias</option>
          {cats.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="text-sm text-[#C9A84C] border border-[#C9A84C]/30 bg-[#C9A84C]/10 px-3 py-2 mb-4">{error}</div>
      )}

      <div className="space-y-6">
        {groups.map((c) => (
          <div key={c.id} className="card overflow-x-auto" data-testid={`op-cat-group-${c.id}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="font-display font-black text-lg tracking-tight">
                <span className="text-[#C9A84C] mr-2">#{c.ordem}</span>{c.nome}
              </div>
              <span className="chip chip-admin">{c.athletes.length} atleta(s)</span>
            </div>
            <table className="table-dense">
              <thead>
                <tr>
                  <th className="w-14">Nº</th>
                  <th className="w-16">Ordem</th>
                  <th>Atleta</th>
                  <th>Equipe</th>
                  <th>Treinador</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {c.athletes.map((a) => (
                  <tr key={a.id} data-testid={`op-athlete-row-${a.id}`}>
                    <td className="tabular-nums font-display font-bold text-lg">{a.numero}</td>
                    <td className="tabular-nums text-[var(--text-secondary)]">{a.ordem_palco ?? "—"}</td>
                    <td className="font-semibold">{a.nome}</td>
                    <td className="text-xs text-[var(--text-secondary)]">{a.equipe || "—"}</td>
                    <td className="text-xs text-[var(--text-secondary)]">{a.treinador || "—"}</td>
                    <td>{a.is_dobra && <span className="chip chip-judge">DOBRA</span>}</td>
                  </tr>
                ))}
                {c.athletes.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-5 text-[var(--text-secondary)]">Nenhum atleta nesta categoria.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
        {groups.length === 0 && (
          <div className="card p-8 text-center text-[var(--text-secondary)]">Nenhum resultado.</div>
        )}
      </div>
    </div>
  );
}
